'use client';

import { forwardRef, type ReactElement, type RefAttributes } from 'react';
import Box from '@mui/material/Box';
import NoSsr from '@mui/material/NoSsr';
import { Icon } from '@iconify/react';

import { iconifyClasses } from './classes';
import type { IconifyProps } from './types';

const IconifyRoot = forwardRef<SVGElement, IconifyProps>(
  function Iconify({ className, width = 20, sx, icon, color, ...other }, ref): ReactElement {
    const baseStyles = {
      width,
      height: width,
      flexShrink: 0,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: color as any,
    } as const;

    const iconKey = typeof icon === 'string' ? icon : undefined;

    const renderFallback = (
      <Box
        component="span"
        className={iconifyClasses.root.concat(className ? ` ${className}` : '')}
        sx={{ ...baseStyles, ...sx }}
      />
    );

    if (!iconKey) {
      return renderFallback;
    }

    return (
      <NoSsr fallback={renderFallback}>
        <Box
          ref={ref as any}
          component="span"
          className={iconifyClasses.root.concat(className ? ` ${className}` : '')}
          sx={{ ...baseStyles, ...sx }}
          {...other as any}
        >
          <Icon icon={iconKey} style={{ width, height: width, color: (baseStyles as any).color }} />
        </Box>
      </NoSsr>
    );
  }
);

/**
 * `forwardRef` is typed as `ExoticComponent` → `(props) => ReactNode` in `@types/react`’s TS5+ entry; stricter JSX
 * rules reject that for component position. Annotate the export as a plain render function (runtime unchanged).
 */
export const Iconify: (props: IconifyProps & RefAttributes<SVGElement>) => ReactElement | null =
  IconifyRoot as unknown as (props: IconifyProps & RefAttributes<SVGElement>) => ReactElement | null;
